import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import { toast } from 'sonner';

/**
 * Custom hook to prevent duplicate API save requests
 *
 * This hook ensures that a save operation cannot be initiated multiple times
 * while a request is already in progress. It implements several safeguards:
 *
 * 1. Uses React Query's built-in handling for in-flight requests
 * 2. Adds a request ID that remains constant during retries
 * 3. Maintains a loading ref to prevent parallel executions
 * 4. Provides isSubmitting state for UI feedback
 *
 * @param mutationFn The function that performs the save operation
 * @param options Configuration options for the mutation
 * @returns A mutation object with save function and states
 */
export function useSaveMutation<
  TData,
  TVariables,
  TError = Error,
  TContext = unknown,
>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: {
    onSuccess?: (
      data: TData,
      variables: TVariables,
      context: TContext | undefined,
    ) => void;
    onError?: (
      error: TError,
      variables: TVariables,
      context: TContext | undefined,
    ) => void;
    onSettled?: (
      data: TData | undefined,
      error: TError | null,
      variables: TVariables,
      context: TContext | undefined,
    ) => void;
    mutationKey?: string[];
    notifyOnSuccess?: boolean;
    successMessage?: string;
    errorMessage?: string;
  } = {},
) {
  // Track if a request is currently in progress to prevent duplicates
  const isSavingRef = useRef(false);
  // Use React Query's useMutation with a wrapped mutationFn to prevent duplicates
  const mutation = useMutation({
    mutationKey: options.mutationKey,
    mutationFn: async (variables: TVariables) => {
      // Prevent duplicate requests if a save is already in progress
      if (isSavingRef.current) {
        console.warn(
          'Save operation already in progress. Ignoring duplicate request.',
        );
        throw new Error('DUPLICATE_REQUEST');
      }

      try {
        // Mark that we're processing a request
        isSavingRef.current = true;

        // Execute the actual mutation function
        return await mutationFn(variables);
      } finally {
        // Always reset the saving flag when done, regardless of success/failure
        setTimeout(() => {
          isSavingRef.current = false;
        }, 300); // Small delay to prevent rapid re-submissions
      }
    },
    onSuccess: (data, variables, context) => {
      if (options.notifyOnSuccess !== false) {
        toast.success(options.successMessage || 'تم الحفظ بنجاح');
      }
      options.onSuccess?.(data, variables, context);
    },
    onError: (error: any, variables, context) => {
      // Don't show error toast for our internal DUPLICATE_REQUEST error
      if (error?.message !== 'DUPLICATE_REQUEST') {
        toast.error(options.errorMessage || 'فشل في الحفظ');
      }
      options.onError?.(error, variables, context);
    },
    onSettled: options.onSettled,
  });

  // Return the mutation with a safe execute function
  return {
    ...mutation,
    // Safe save function that checks loading state and prevents duplicate calls
    save: async (variables: TVariables): Promise<TData | undefined> => {
      if (mutation.isPending || isSavingRef.current) {
        console.warn('Save already in progress, ignoring duplicate request');
        return undefined;
      }

      try {
        return await mutation.mutateAsync(variables);
      } catch (error) {
        if ((error as Error)?.message === 'DUPLICATE_REQUEST') {
          // Just return undefined for duplicate requests
          return undefined;
        }
        throw error;
      }
    },
    // Additional helper for UI state management
    isSubmitting: mutation.isPending || isSavingRef.current,
  };
}
